/**
 * BuildImageDialog — Confirmation modal for dispatching an image-build via
 * POST /api/images/build. NC-498 Phase 3.
 *
 * Operator triggers this from WorkloadEditorPage. The dialog warns about
 * runtime (~60–90 min) and cost (~€5–10), offers a dry-run toggle so the
 * Tutor can sanity-check inputs without burning compute, and surfaces the
 * 429-Quota / 503-Setup / 502-GitHub errors inline.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { imageBuildsApi } from '../services/imageBuildsService';
import { imageReadinessApi, WorkloadReadiness } from '../services/imageReadinessService';
import { userSettingsApi } from '../services/userSettingsService';
import { costCentersApi } from '../services/costCentersService';
import { CostCenterDefinition } from '../services/types';

interface BuildImageDialogProps {
  open: boolean;
  workloadId: string;
  workloadName?: string;
  /**
   * NC-984 (F2): when true this workload is buildable but its images stay in
   * staging (never promotable to production). The dialog then explains that
   * BEFORE dispatch so the operator isn't surprised later.
   */
  stagingOnly?: boolean;
  onClose: () => void;
  /** Called with the newly-created buildId so the caller can navigate to the detail page. */
  onDispatched: (buildId: string) => void;
}

export default function BuildImageDialog({
  open,
  workloadId,
  workloadName,
  stagingOnly = false,
  onClose,
  onDispatched,
}: BuildImageDialogProps) {
  const [dryRun, setDryRun] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notificationHint, setNotificationHint] = useState(false);

  // NC-867 T5: the image build is a chargeable asset and must name an owning
  // cost-center. Auto-select the sole active one; a free-text purpose is optional.
  const [costCenters, setCostCenters] = useState<CostCenterDefinition[]>([]);
  const [ownerCostCenter, setOwnerCostCenter] = useState('');
  const [purpose, setPurpose] = useState('');

  // NC-967: installer-readiness Ampel shown BEFORE dispatch. The BE gate rejects
  // a non-ready build (incl. dry-run) with a 400, so surfacing the concrete gaps
  // here saves a guaranteed-failed click. `readiness === null` while loading;
  // `readinessError` is advisory-only (fail-open — the BE still enforces).
  const [readiness, setReadiness] = useState<WorkloadReadiness | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState(false);

  // NC-505 §2.3: one-time hint that Teams notifications exist — builds run
  // 60–90 min, so users who never opted in only find out afterwards. Shown
  // until notifications are on or the hint is dismissed; settings-fetch
  // failures (container missing, offline) silently suppress the hint.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    userSettingsApi
      .get()
      .then((s) => {
        if (cancelled) return;
        setNotificationHint(
          !s.notifications.imageBuild && !s.hints.imageBuildNotificationToastDismissed
        );
      })
      .catch(() => {
        if (!cancelled) setNotificationHint(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // NC-967: fetch installer readiness whenever the dialog opens for a workload.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setReadiness(null);
    setReadinessError(false);
    setReadinessLoading(true);
    imageReadinessApi
      .get(workloadId)
      .then((r) => {
        if (!cancelled) setReadiness(r);
      })
      .catch(() => {
        // Advisory only — don't block the build on a failed readiness probe;
        // the BE dispatch gate is the real enforcement.
        if (!cancelled) setReadinessError(true);
      })
      .finally(() => {
        if (!cancelled) setReadinessLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workloadId]);

  // NC-867 T5: load active cost-centers on open; pre-select the sole active one
  // (current reality: IT-Plattform). With multiple, an explicit choice is required.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    costCentersApi
      .list({ active: true })
      .then((data) => {
        if (cancelled) return;
        setCostCenters(data);
        setOwnerCostCenter((prev) => (prev === '' && data.length === 1 ? data[0].code : prev));
      })
      .catch(() => {
        if (!cancelled) setCostCenters([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleDismissHint = () => {
    setNotificationHint(false);
    // Best-effort persistence — a failed PUT just means the hint reappears.
    userSettingsApi
      .update({ hints: { imageBuildNotificationToastDismissed: true } })
      .catch(() => {});
  };

  // Block the confirm button only once readiness has RESOLVED to not-ready — a
  // non-ready dispatch would just 400. During the (brief) load and on a fetch
  // error we fail-open and let the BE gate enforce, so the button doesn't
  // flicker-disable for the common ready case.
  const notReady = !!readiness && !readiness.ready;
  // NC-867 T5: also block until an owner cost-center is chosen (mandatory).
  const buildBlocked = notReady || ownerCostCenter === '';

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    setDryRun(false);
    setPurpose('');
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await imageBuildsApi.build({
        workloadId,
        dryRun,
        ownerCostCenter,
        ...(purpose.trim() ? { purpose: purpose.trim() } : {}),
      });
      onDispatched(result.buildId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Build konnte nicht gestartet werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const title = workloadName
    ? `Image bauen: ${workloadName}`
    : `Image bauen: ${workloadId}`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="build-dialog-title">
      <DialogTitle id="build-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Der Build läuft als Tier-3-Final-Pipeline gegen die <strong>staging</strong>-Gallery.
          {stagingOnly
            ? ' Dieser Workload wird nicht nach production promotet.'
            : ' Promotion auf production ist Admin-only.'}
        </DialogContentText>

        {stagingOnly && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="staging-only-notice">
            <Typography variant="body2">
              Hinweis: <strong>{workloadName || workloadId}</strong>-Images bleiben dauerhaft in
              staging und können <strong>nicht</strong> nach production promotet werden. Sie eignen
              sich zum Testen der Pipeline, nicht für Trainings-Runs.
            </Typography>
          </Alert>
        )}

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ⏱️ Dauer ca. 60–90 Minuten. 💶 Kosten ca. €5–10 pro Lauf.
          </Typography>
        </Alert>

        {/* NC-967: installer-readiness Ampel before dispatch. */}
        {readinessLoading && (
          <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2 }} data-testid="readiness-loading">
            <Typography variant="body2">Prüfe Installer-Readiness…</Typography>
          </Alert>
        )}

        {!readinessLoading && readiness && readiness.ready && (
          <Alert severity="success" sx={{ mb: 2 }} data-testid="readiness-ready">
            <Typography variant="body2">Alle deklarierten Installer sind bereit.</Typography>
          </Alert>
        )}

        {!readinessLoading && notReady && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="readiness-not-ready">
            <Typography variant="body2" fontWeight="bold">
              Nicht build-bereit — fehlende Installer:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {readiness!.apps
                .filter((a) => !a.ready)
                .map((a) => (
                  <li key={a.applicationId}>
                    <Typography variant="body2" component="span">
                      {a.displayName}: {a.reason || 'kein validierter Installer'}
                    </Typography>
                  </li>
                ))}
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Lade die fehlenden Installer unter{' '}
              <RouterLink to="/installers">Installers</RouterLink> hoch, bevor du baust.
            </Typography>
          </Alert>
        )}

        {!readinessLoading && readinessError && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="readiness-error">
            <Typography variant="body2">
              Installer-Readiness konnte nicht vorab geprüft werden. Der Server prüft sie beim
              Start erneut.
            </Typography>
          </Alert>
        )}

        {notificationHint && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={handleDismissHint}>
            Tipp: Aktiviere Teams-Benachrichtigungen für den Build-Abschluss in den{' '}
            <RouterLink to="/settings">Einstellungen</RouterLink>.
          </Alert>
        )}

        {/* NC-867 T5: mandatory owner cost-center + optional purpose. */}
        <TextField
          select
          fullWidth
          required
          label="Kostenstelle (Owner)"
          value={ownerCostCenter}
          onChange={(e) => setOwnerCostCenter(e.target.value)}
          disabled={submitting}
          sx={{ mb: 2 }}
          helperText={
            costCenters.length === 0
              ? 'Keine aktive Kostenstelle verfügbar — bitte zuerst eine Kostenstelle anlegen'
              : 'Pflichtfeld — das Image wird dieser Kostenstelle zugeordnet'
          }
        >
          {costCenters.map((cc) => (
            <MenuItem key={cc.code} value={cc.code}>
              {cc.displayName} ({cc.code})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Zweck (optional)"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          disabled={submitting}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 500 }}
          helperText="Wozu wird dieses Image gebaut? (optional, max. 500 Zeichen)"
        />

        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                disabled={submitting}
                inputProps={{ 'aria-label': 'Dry-Run' }}
              />
            }
            label="Validate-only (Dry-Run, kein Image-Capture)"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Abbrechen
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={submitting || buildBlocked}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Starte…' : dryRun ? 'Dry-Run starten' : 'Build starten'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
